import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/root";

interface FloatingActionButtonProviderProps {
  children: ReactNode;
}

interface FloatingActionButtonContextType {
  isGettingStartedFABVisible: boolean;
  setIsGettingStartedFABVisible: (value: boolean) => void;
}

const FloatingActionButtonContext = React.createContext<
  FloatingActionButtonContextType | undefined
>(undefined);

const GETTING_STARTED_FAB_LOCAL_STORAGE_KEY = "gettingStartedCollapsed";

const DAYS_TO_ENABLE_GETTING_STARTED = 2;

function isGettingStartedEnabled(teamTimeCreated: number) {
  const timeSinceCreated = Date.now() - teamTimeCreated;
  const daysSinceCreated = Math.ceil(timeSinceCreated / (1000 * 60 * 60 * 24));

  return (
    daysSinceCreated <= DAYS_TO_ENABLE_GETTING_STARTED ||
    !!new URLSearchParams(window.location.search).get("test_getting_started")
  );
}

function FloatingActionButtonProvider({
  children,
}: FloatingActionButtonProviderProps) {
  const { team } = useSelector((state: RootState) => state.team);

  const [isGettingStartedFABVisible, setIsGettingStartedFABVisible] =
    useState(false);

  const _setIsGettingStartedFABVisible = useCallback(
    (value: boolean) => {
      // if getting started is not enabled, then we don't want to show the FAB
      if (!isGettingStartedEnabled(team.value?.time_created || 0)) {
        setIsGettingStartedFABVisible(false);
        localStorage.removeItem(GETTING_STARTED_FAB_LOCAL_STORAGE_KEY);
        return;
      }

      if (value) {
        localStorage.setItem(GETTING_STARTED_FAB_LOCAL_STORAGE_KEY, "1");
      } else {
        localStorage.removeItem(GETTING_STARTED_FAB_LOCAL_STORAGE_KEY);
      }

      setIsGettingStartedFABVisible(value);
    },
    [team.value?.time_created, setIsGettingStartedFABVisible],
  );

  // Set initial visibility
  useEffect(() => {
    _setIsGettingStartedFABVisible(
      !!localStorage.getItem(GETTING_STARTED_FAB_LOCAL_STORAGE_KEY),
    );
  }, [_setIsGettingStartedFABVisible]);

  return (
    <FloatingActionButtonContext.Provider
      value={{
        isGettingStartedFABVisible,
        setIsGettingStartedFABVisible: _setIsGettingStartedFABVisible,
      }}
    >
      {children}
    </FloatingActionButtonContext.Provider>
  );
}

function useFloatingActionButtonContext() {
  const context = useContext(FloatingActionButtonContext);

  if (context === undefined) {
    throw new Error(
      "useFloatingActionButtonContext must be used within a FloatingActionButtonProvider",
    );
  }

  return context;
}

export {
  FloatingActionButtonProvider,
  useFloatingActionButtonContext,
  isGettingStartedEnabled,
};
